// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import StepWizard from 'react-step-wizard';
import KeyWorkflowTagsStep from './wizard/KeyWorkflowTagsStep';
import KeyWorkflowFreeTextTagsStep from './wizard/KeyWorkflowFreeTextTagsStep';
import SelectCompanyStep from './wizard/SelectCompanyStep';
import SelectGameStep from './wizard/SelectGameStep';
import SelectKeyWorkflowStep from './wizard/SelectKeyWorkflowStep';
import SelectSKUStep from './wizard/SelectSKUStep';
import useRequestKeysWizard from '../../../hooks/useRequestKeysWizard';
import ConfirmStep from './wizard/ConfirmStep';
import Loading from '../../common/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WizardSteps from '../../../constants/WizardSteps';
import SlideoutLayout from '../../layout/slideout/SlideoutLayout';
import { useTranslation } from 'react-i18next';
import icons from '../../../constants/icons';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { saveRequest } from '../../../reducers/basketReducer';
import SlidingPane from 'react-sliding-pane';
import UploadAccountDetails from './UploadAccountDetails';
import { downloadAccountDetailsTemplate } from '../../../api/requestApi';

export interface RequestKeysProps {
  done: () => void;
  cancel: () => void;
  gameId?: string;
  skuId?: string;
  handleUploadAccountDetails?: (id: string, numberOfKeys: number) => void;
}

function RequestKeys({
  done,
  cancel,
  gameId,
  skuId,
  handleUploadAccountDetails,
}: RequestKeysProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth);
  const [uploadAccountDetailsOpen, setUploadAccountDetailsOpen] =
    useState(false);
  const [uploadAccountDetail, setUploadAccountDetail] = useState<any>();

  const [
    currentStep,
    companies,
    setCompanyId,
    keyWorkflows,
    setKeyWorkflowId,
    games,
    setGameId,
    skus,
    setSKUId,
    setRequesterTag,
    requesterTags,
    setRequesterTagId,
    keyProviderRequestInformation,
    request,
    handleBack,
    isLoading,
    setIsLoading,
    isFirstItem,
  ] = useRequestKeysWizard({
    currentUser,
    _gameId: gameId,
    _skuId: skuId,
  });
  const [wizard, setWizard] = useState<any>();
  useEffect(() => {
    if (wizard) {
      wizard.goToNamedStep(currentStep);
    }
  }, [currentStep, wizard]);

  const handleCompanySelect = (value: string) => setCompanyId(value);
  const handleKeyWorkflowSelect = (value: string) => setKeyWorkflowId(value);
  const handleGameSelect = (value: string) => setGameId(value);
  const handleSKUSelect = (value: string) => setSKUId(value);
  const handleKeyWorkflowFreeTextTagInput = (data: { keyWorkflowTag: string }) =>
    setRequesterTag(data.keyWorkflowTag);
  const handleKeyWorkflowTagSelect = (value: string) => setRequesterTagId(value);

  const onSubmit = (data: any) => {
    setIsLoading(true);
    dispatch(saveRequest(data))
      .unwrap()
      .then((response: any) => {
        if (response.endUserDetailsRequired) {
          if (handleUploadAccountDetails) {
            handleUploadAccountDetails(response.id, response.numberOfKeys);
          } else {
            // If the parent didn't supply a handler, open the upload slideout here.
            setUploadAccountDetail({
              keyRequestId: response.id,
              numberOfKeys: response.numberOfKeys,
            });
            setUploadAccountDetailsOpen(true);
            toast.success(t('Request added to basket'));
            return;
          }
        }
        toast.success(t('Request added to basket'));
        done();
      })
      .catch((error: any) => {
        setIsLoading(false);
        toast.error(error?.message ?? t('Failed to add request to basket'));
      });
  };

  const custom = {
    enterRight: 'animated fadeInRight',
    enterLeft: 'animated fadeInLeft',
    exitRight: 'animated fadeOutRight',
    exitLeft: 'animated fadeOutLeft',
  };

  const isConfirmStep = currentStep === WizardSteps.CONFIRM;
  const showSteps = !isFirstItem || !isConfirmStep;
  const detailsActive = !isFirstItem && !isConfirmStep;

  return currentStep ? (
    <SlideoutLayout title={t('Request keys')} cancel={cancel} hideBar={true}>
      <header className="clearfix width-100">
        <div className="width-100 d-flex wizard-steps">
          {showSteps ? (
            <>
              <div
                className={`d-flex align-items-center back-btn ${
                  isFirstItem ? 'disabled' : ''
                }`}
                style={{ visibility: isFirstItem ? 'hidden' : 'visible' }}
                title={t('Back')}
                aria-label={t('Go back')}
                onClick={() => {
                  if (!isFirstItem) handleBack();
                }}
              >
                <div className="icon-wrapper">
                  <FontAwesomeIcon icon={icons.CHEVRON_LEFT} className="a" />
                </div>
              </div>
              <div className={`step ${isFirstItem ? 'active' : ''}`}>{t('Start')}</div>
              <div className={`step ${detailsActive ? 'active' : ''}`}>{t('Details')}</div>
              <div
                className={`step me-4 ${
                  !isFirstItem && isConfirmStep ? 'active' : ''
                }`}
              >
                {t('Basket')}
              </div>
            </>
          ) : (
            <div className="step me-4 active">{t('Basket')}</div>
          )}
        </div>
        <i
          className="a fas fa-times fa-2x me-2"
          title={t('Close')}
          aria-label={t('Close panel')}
          onClick={cancel}
        />
      </header>

      <div className="content">
        {currentUser && !isLoading ? (
          <StepWizard
            instance={setWizard}
            transitions={custom}
            isLazyMount={true}
          >
            <SelectCompanyStep
              stepName="company"
              currentUser={currentUser}
              companies={companies}
              handleCompanySelect={handleCompanySelect}
            />
            <SelectKeyWorkflowStep
              stepName="keyWorkflow"
              keyWorkflows={keyWorkflows}
              handleKeyWorkflowSelect={handleKeyWorkflowSelect}
            />
            <SelectGameStep
              stepName="game"
              games={games}
              handleGameSelect={handleGameSelect}
            />
            <SelectSKUStep
              stepName="sku"
              skus={skus}
              handleSKUSelect={handleSKUSelect}
            />
            <KeyWorkflowTagsStep
              stepName="tags"
              requesterTags={requesterTags}
              handleRequesterTagSelect={handleKeyWorkflowTagSelect}
            />
            <KeyWorkflowFreeTextTagsStep
              stepName="tagsFreeText"
              handleKeyWorkflowFreeTextTagInput={handleKeyWorkflowFreeTextTagInput}
            />
            <ConfirmStep
              stepName="confirm"
              request={request}
              keyProviderRequestInformation={keyProviderRequestInformation}
              saveRequest={onSubmit}
            />
          </StepWizard>
        ) : (
          <Loading />
        )}
      </div>
      <SlidingPane
        isOpen={uploadAccountDetailsOpen}
        hideHeader={true}
        from="right"
        className="large-side-panel"
        onRequestClose={() => setUploadAccountDetailsOpen(false)}
      >
        <UploadAccountDetails
          id={uploadAccountDetail?.keyRequestId}
          numberOfKeys={uploadAccountDetail?.numberOfKeys}
          done={() => {
            // After uploading (or saving) account details, continue to basket confirm.
            setUploadAccountDetailsOpen(false);
            done();
          }}
          cancel={() => {
            setUploadAccountDetailsOpen(false);
            done();
          }}
          downloadAccountDetailsTemplate={() =>
            downloadAccountDetailsTemplate().catch(() => {
              toast.error(t('Failed to download account details template'));
            })
          }
        />
      </SlidingPane>
    </SlideoutLayout>
  ) : (
    <Loading />
  );
}

export default RequestKeys;
